import { AppConstants } from 'cd-eft/appConstants';
import { ElevationInterpolater } from 'cd-eft/components/SurveyEngineering/Maps/SETMapFunctions/elevationInterpolationEngine';
import { IDisposable } from 'cd-eft/framework/Disposer';
import {
  esriGeometryEngine,
  Extent,
  FeatureLayer,
  Graphic,
  GraphicsLayer,
  MapView,
  Point,
  Polyline,
  SketchViewModel,
  SpatialReference,
} from 'cd-eft/utils/esriTypes';
import { action, IReactionDisposer, reaction } from 'mobx';
import { XyzPoint } from '../../SurveyEngineering/XyzPoint';
import { ProfileKeyCode, SketchState } from './DynamicProfileEnums';
import { DynamicProfileClass } from './DynamicProfileClass';
import { DynamicProfileHubBase } from './DynamicProfileHubBase';
import Stores from '..';
import { ProfileRequestDTO } from 'cd-eft/_codegen_/openapi-generator/eft-set';
import { ProfileController, TerraceProfileController, } from 'cd-eft/services/eftSetApi';
import { reachLineSymbol, channelLineSymbol, pointLabelFields, pointLabelClass, reachStationPointRenderer, invisibleRenderer, pointLabelClass2, netsLegendDarkRed, editableStationPointRenderer, channelStationSymbol } from 'cd-eft/models/Stores/DynamicProfile/profileSymbolsAndLabels';
import DrainIcon from '../../../../src/assets/images/drain-black.svg';
import BlockIcon from '../../../../src/assets/images/block-black.svg';
import { doEvents } from 'cd-eft/utils/sysUtils';
import { putStructureRepOnTop } from 'cd-eft/utils/gisUtils';
import { parseStationString, formatStationString } from 'cd-eft/utils/stationUtilities';
import MessageCenter from 'cd-eft/components/common/MessageCenter/MessageCenterVM';
import { DynamicTerraceMapStore } from './Terrace/DynamicTerraceMapStore';
import GraphicsLayerView from '@arcgis/core/views/layers/GraphicsLayerView';
import Multipoint from '@arcgis/core/geometry/Multipoint';

const { designStore, waterwayDesignStore, terraceDesignStore } = Stores.AppStore;
type componentType = 'alignment' | 'singleCrossSection' | 'waterway' | 'threeCrossSection1' | 'threeCrossSection2' | 'threeCrossSection3' | 'terrace' | 'UGO';
abstract class DynamicProfileStoreBase implements IDisposable {
  protected hub: DynamicProfileHubBase | undefined;
  protected _isValidLine = true;
  protected _loopThreshold = 0;
  protected sketchLayer: GraphicsLayer | undefined = void 0;

  public _profileView: MapView | undefined = void 0;
  private h_grid: FeatureLayer | undefined = void 0;
  private v_grid: FeatureLayer | undefined = void 0;
  private h_label: FeatureLayer | undefined = void 0;
  private v_label: FeatureLayer | undefined = void 0;
  public maxExtent: Extent | undefined = void 0;
  public origExtent: Extent | undefined = void 0;
  private _viewPointerMoveHandle;
  private _viewPointerLeaveHandle;
  protected _centerlineStations;
  protected _lowBankStations;
  protected _leftBankStations;
  protected _rightBankStations;
  public alignmentCenterLine = [] as number[][];
  protected _terraceDrainsLayer: GraphicsLayer | undefined = void 0;
  protected _terraceBlocksLayer: GraphicsLayer | undefined = void 0;
  protected _waterLevelLayer: GraphicsLayer | undefined;
  protected _constructedTerraceLayer: GraphicsLayer | undefined;
  protected _settledTerraceLayer: GraphicsLayer | undefined;
  protected _groundSurfaceProfileline: Polyline | undefined;
  protected profileImmediateClickHandle;
  protected profilePointerMoveHandle;
  protected feedbackLayer = new GraphicsLayer({ title: 'pointsFeedbackLayer', listMode: 'hide' });
  protected channelStationMP: Multipoint = new Multipoint();

  protected maxXmin: number = 10000000;
  protected maxXmax: number = -1000;
  protected maxYmin: number = 10000000;
  protected maxYmax: number = -1000;
  private x_padding: number = 0;
  public y_padding: number = 0;
  private h_gridSize: number = 0;
  private v_gridSize: number = 0;

  /** guardrail */
  private guardrailMinY: number = 0;
  private guardrailMaxY: number = 0;

  protected mapSpatialReference: SpatialReference | undefined = void 0;
  protected dataSpatialReference: SpatialReference | undefined = void 0;
  protected mapAlignmentSet: boolean = false;

  /** reaction disposers */
  private newVertexReactionDisposer: IReactionDisposer;
  private mapSketchReationDispoer: IReactionDisposer;
  private insertVertexReactionDisposer: IReactionDisposer;
  private moveVertexReactionDisposer: IReactionDisposer;
  private deleteVertexReactionDisposer: IReactionDisposer;
  private sketchCancelReactionDisposer: IReactionDisposer;

  /** multiple profiles */
  public editable = false;
  protected profileKeys: ProfileKeyCode[] = [ProfileKeyCode.DEFAULT];
  protected profiles = {};
  protected profileData = {};
  protected mainSketchVm: SketchViewModel | undefined = void 0;
  protected elevationInterpolater: ElevationInterpolater = ElevationInterpolater.Current;

  private threeCrossSection?: boolean;
  protected threeCrossSectionStartDist: number = 0;
  protected threeCrossSectionEndDist: number = 0;
  protected threeCrossSectionBufferWidth: number = 10;
  private componentType: componentType | undefined;
  /** ctor */
  constructor() {

    // communication pattern 1: broadcast/observer pattern
    this.mapSketchReationDispoer = reaction(
      () => this.hub?.MapSketchState.state === SketchState.START,
      (isStarted) => {
        if (isStarted) this.reset(this.hub?.MapSketchState.keyCode);
      }
    );

    this.newVertexReactionDisposer = reaction(
      () => this.hub?.NewMapVertexInfo,
      async (info) => {
        if (info && (ElevationInterpolater.Current || designStore.currentSurface.surfaceDefClassName !== 'survey'))
          await this.interpolateSurfaceNewVertex(info.z, info.x, info.y, info.keyCode, info.blockProfileForTable, info.blockProfile2dAlignment);
      }
    );

    this.insertVertexReactionDisposer = reaction(
      () => this.hub?.InsertMapVertexInfo,
      async (info) => {
        if (info && (ElevationInterpolater.Current || designStore.currentSurface.surfaceDefClassName !== 'survey'))
          await this.insertVertex(info.newVertex, info.vertexIndex, info.keyCode, info.blockModifyProfile, info.alignmentObject);
      }
    );

    this.moveVertexReactionDisposer = reaction(
      () => this.hub?.MoveMapVertexInfo,
      async (info) => {
        if (info && (ElevationInterpolater.Current || designStore.currentSurface.surfaceDefClassName !== 'survey'))
          await this.modifyVertex(info.newVertex, info.vertexIndex, info.keyCode, info.blockModifyProfile, info.alignmentObject);
      }
    );

    this.deleteVertexReactionDisposer = reaction(
      () => this.hub?.ProfileVertexIndexToDeleteInfo,
      async (info) => {
        if (info && (ElevationInterpolater.Current || designStore.currentSurface.surfaceDefClassName !== 'survey')) { await this.deleteVertex(info) }
      }
    );

    this.sketchCancelReactionDisposer = reaction(
      () => this.hub?.MapSketchState.state === SketchState.CANCEL,
      (isCanceled) => {
        if (isCanceled) this.reset(this.hub?.MapSketchState.keyCode);
      }
    );

    this.mainSketchVm = new SketchViewModel();
  }

  initialize(profileView: MapView, spatialReference: SpatialReference, editable: boolean, componentType: componentType): void {
    this.componentType = componentType;
    if (componentType === 'threeCrossSection1' || componentType === 'threeCrossSection2' || componentType === 'threeCrossSection3') {
      this.threeCrossSection = true;
    }
    this.profileView = profileView;
    this.mapSpatialReference = this.mainMapView?.spatialReference;
    this.dataSpatialReference = spatialReference;
    this.editable = editable;

    this.profileKeys.forEach((keyCode) => {
      const profile = new DynamicProfileClass(
        profileView,
        keyCode,
        this.dataSpatialReference!,
        this.mapSpatialReference!,
        this.h_grid!,
        this.v_grid!,
        this.componentType!
      );
      this.initData(profile, keyCode);
      profile.newMaxExtent = (newExtent: Extent) => {
        if (!(this.threeCrossSection && keyCode === 2)) {
          this.maxExtent = this.origExtent = newExtent;
        }
        this.updateGridLineLayers(newExtent, keyCode);
      };
      this.initDraw(profile);
      this.profiles[ProfileKeyCode[keyCode]] = profile;
    });

    if (this._profileView) this._profileView.highlightOptions = { haloOpacity: 0, fillOpacity: 0 };

    this.initSketchVm();
  }

  protected get mainMapView(): MapView | undefined {
    return this.hub?.MapStore?.MapView;
  }

  public deleteAction: ((index: number) => void) | undefined = undefined;

  // ******* Waterway and Terrace common functions ********
  protected abstract handlePolylineCreateEvent(event): void;
  protected abstract handlePointCreateEvent(event): void;
  protected abstract handleReshapeEvent(event): void;
  protected abstract setUpTerraceAlignmentView(stations, xStretchFactor, yStretchFactor): void;
  abstract updateUgoTableOnChangeStationValue(point, vertexIndex): Promise<void>;
  abstract createChannelFromSavedUgo(): Promise<void>;
  abstract redrawUgoProfiles(): Promise<void>;

  async deletePoint(removeStartEndStations: boolean = true) {
    this.clearDelete();
    this.mainSketchVm?.complete();
    if (!removeStartEndStations) {
      this.generateReachStationLayer();
    } else {
      this.generateReachStationLayer(true);
    }
    // Create point layer of reach stations
    if (this._profileView && this.mainSketchVm?.layer && this.mainSketchVm.layer.graphics && this.mainSketchVm.layer.graphics.length > 0) {
      const line = this.mainSketchVm.layer.graphics.getItemAt(0).geometry as Polyline;
      const path = line.paths[0];
      const length = path.length;
      const pointSource = [] as Graphic[];

      for (let i = 1; i < length; i++) {
        const point = new Graphic({
          geometry: new Point({
            spatialReference: line.spatialReference,
            x: path[i][0],
            y: path[i][1],
          }),
          attributes: {
            ObjectID: i,
          },
        });
        // console.log("file: DynamicTerraceProfileStore.ts:620 ~ DynamicTerraceProfileStore ~ deletePoint ~ point:", JSON.stringify(point))
        pointSource.push(point);
      }

      const pointsLayer = new FeatureLayer({
        source: pointSource,
        renderer: editableStationPointRenderer as any,
        id: 'deleteReachStations',
        objectIdField: 'ObjectID',
      });

      this._profileView.map.add(pointsLayer);
      this._profileView.highlightOptions = {
        haloOpacity: 1,
        fillOpacity: 1,
      };

      let highlight: any;

      try {
        const options: __esri.MapViewHitTestOptions = {};
        options.include = pointsLayer;

        const highlightGraphics = async (e: any): Promise<void> => {
          if (highlight) {
            highlight.remove();
            highlight = undefined;
            return;
          }

          const result = await this._profileView?.hitTest(e, options);
          if (result?.results[0]) {
            const graphic = result.results[0].graphic;

            if (highlight) {
              highlight.remove();
              highlight = null;
              return;
            }

            if (highlight) {
              return;
            }

            if (this._profileView) {
              for (const layerView of this._profileView.allLayerViews) {
                if (layerView.layer === graphic.layer) {
                  highlight = (layerView as GraphicsLayerView).highlight(graphic);
                }
              }
            }
          } else {
            // remove the highlight if no features are returned from the hitTest
            if (highlight) {
              highlight.remove();
              highlight = null;
            }
          }
        };

        const getGraphics = async (response: any): Promise<void> => {
          const result = await this._profileView?.hitTest(response, options);
          if (result?.results[0]) {
            const graphic = result.results[0].graphic;

            const attributes = graphic.attributes;
            const id = attributes.ObjectID;

            setTimeout(() => {
              if (this.deleteAction) this.deleteAction(id);
            }, 100);
          } else {
            return;
          }
        }

        this.profileImmediateClickHandle = this._profileView!.on('immediate-click', getGraphics);
        this.profilePointerMoveHandle = this._profileView!.on('pointer-move', highlightGraphics);

      } finally {
        // make sure to cleanup the highlight handle
        if (highlight) {
          highlight.remove();
          highlight = undefined;
        }
      }
    }
  }

  protected async handleProfileMove(e: __esri.ViewPointerMoveEvent) {
    this.feedbackLayer.graphics?.removeAll();
    if (this._profileView) {
      const point = this._profileView.toMap(e);
      const findResult = esriGeometryEngine.nearestVertex(this.channelStationMP, point);

      if (findResult) {
        const resultPoint = new Point({ x: findResult.coordinate.x, y: findResult.coordinate.y, spatialReference: this._profileView.spatialReference });
        const nextPointGraphic = new Graphic({
          geometry: resultPoint,
          symbol: channelStationSymbol.highlightedStationSymbol
        });
        this.feedbackLayer.add(nextPointGraphic);
      }
    }
  }

  protected clearDelete() {
    this.profileImmediateClickHandle?.remove();
    this.profilePointerMoveHandle?.remove();
    const reachStationsLayer = this._profileView?.map.findLayerById('deleteReachStations');
    if (reachStationsLayer) this._profileView?.map.remove(reachStationsLayer);
    if (this._profileView) {
      this._profileView.highlightOptions = {
        haloOpacity: 0,
        fillOpacity: 0,
      }
    }
  }

  async deleteReachVertex(info: any) {
    this.mainSketchVm?.complete();
    this.changePointSymbol('reachStation');
    if (this.sketchLayer && this.sketchLayer.graphics && this.sketchLayer.graphics.length > 0) {
      const currentLine = this.sketchLayer.graphics.getItemAt(0).geometry as Polyline;
      currentLine.removePoint(0, info.index);
      const newLine = new Polyline({
        paths: currentLine.paths,
        spatialReference: currentLine.spatialReference,
      });
      this.sketchLayer.graphics.getItemAt(0).geometry = newLine;
    }
    this.generateReachStationLayer();
    const reachStationsLayer = this._profileView?.map.findLayerById('deleteReachStations') as FeatureLayer;
    if (reachStationsLayer) {
      this.deletePoint(false);
    }
  }

  protected changePointSymbol(type: string): void {
    const reachStationsLayer = this._profileView?.map.findLayerById('reachStations') as FeatureLayer;
    if (reachStationsLayer) {
      reachStationsLayer.renderer = type === 'reachStation' ? reachStationPointRenderer : editableStationPointRenderer as any;
    }
  }

  protected isSnappedOnVertex(point: Point, polyline: Polyline): boolean {
    let retVal = false;
    for (let index = 1; index < polyline.paths[0].length - 1; index++) {
      const vertex = polyline.getPoint(0, index);
      if (esriGeometryEngine.distance(point, vertex) === 0) {
        retVal = true;
        break;
      }
    }
    return retVal;
  }

  protected getStartAndStopPoint(polyline: Polyline): [Point, Point] {
    const startPoint = polyline.getPoint(0, 0);
    const lastIndex = polyline.paths[0].length - 1;
    const stopPoint = polyline.getPoint(0, lastIndex);
    return [startPoint, stopPoint];
  }

  protected isValidFromStart(line: __esri.Polyline) {
    let isValid = true;

    const spatialReference = line.spatialReference;
    const path = line.paths[0];
    for (let index = 2; index < path.length; index++) {
      const pathZero: number[][] = [];
      for (let j = 0; j <= index; j++) {
        pathZero.push(path[j]);
      }
      const checkedSegments = new Polyline({ paths: [pathZero], spatialReference });
      if (!this.isValidLastSegment(checkedSegments)) {
        isValid = false;
        this._isValidLine = false;
        break;
      }
    }
    return isValid;
  }

  protected clearInvalidState(graphic: Graphic | undefined, delay = 0) {
    this._isValidLine = true;
    if (graphic) graphic.symbol = channelLineSymbol.designLineSymbol;

    setTimeout(() => {
      MessageCenter.clearMessages();
    }, delay);
  }

  protected async addPoint() {
    this.mainSketchVm?.complete();
    this.removeReachStationLayer();

    // Create point layer of reach stations
    if (this._profileView && this.mainSketchVm?.layer && this.mainSketchVm.layer.graphics && this.mainSketchVm.layer.graphics.length > 0) {
      const line = this.mainSketchVm.layer.graphics.getItemAt(0).geometry as Polyline;
      const path = line.paths[0];
      const length = path.length;
      const pointSource = [] as Graphic[];

      for (let i = 0; i < length; i++) {
        const point = new Graphic({
          geometry: new Point({
            spatialReference: line.spatialReference,
            x: path[i][0],
            y: path[i][1],
          }),
          attributes: {
            ObjectID: i,
          },
        });
        pointSource.push(point);
      }

      const pointsLayer = new FeatureLayer({
        source: pointSource,
        renderer: editableStationPointRenderer as any,
        id: 'addReachStations',
        objectIdField: 'ObjectID',
      });

      this._profileView.map.add(pointsLayer);
      this._profileView.highlightOptions = {
        haloOpacity: 1,
        fillOpacity: 1,
      };
    }
  }

  async createReachFromSavedDesign(reachStations, alignedDesignElementId) {
    if (this.componentType === "terrace") {
      this.terraceBlocksLayer.removeAll();
      this.terraceDrainsLayer.removeAll();
    }
    const yStretchFactor = designStore.commonVerticalStretchFactor ? designStore.commonVerticalStretchFactor : AppConstants.profileStretchFactor;
    const xStretchFactor = designStore.commonHorizontalStretchFactor ? designStore.commonHorizontalStretchFactor : 1;

    if (alignedDesignElementId && reachStations.length) {
      const drains: [number, number][] = [];
      const blocks: [number, number][] = [];

      const path: [number, number][] = [];
      let minY: number = 10000000;
      let maxY: number = 0;
      reachStations.forEach((channelStation: any, i: number) => {
        if (channelStation.drainStationDto?.drainName) drains.push([parseStationString(channelStation.endStation) * xStretchFactor, parseFloat(channelStation.channelElev) * yStretchFactor]);
        if (channelStation.blockStationDto?.blockHeight) blocks.push([parseStationString(channelStation.endStation) * xStretchFactor, parseFloat(channelStation.channelElev) * yStretchFactor]);

        let stationValue;
        if (channelStation.reachId === "START" || channelStation.reachId === "END") {
          stationValue = channelStation.reachId === "START" ? channelStation.startStation : channelStation.endStation;
        } else {
          stationValue = channelStation.endStation;
        }
        if ((i > 0 && i < reachStations.length - 1) || (i === 0 && channelStation.groundElev > 0) || (i === reachStations.length - 1 && channelStation.groundElev > 0)) {
          path.push([parseStationString(stationValue) * xStretchFactor, parseFloat(channelStation.channelElev) * yStretchFactor]);
          if (channelStation.channelElev < minY) {
            minY = channelStation.channelElev;
          }
          if (channelStation.channelElev > maxY) {
            maxY = channelStation.channelElev;
          }
        }
      });
      await this.generateReachLineFromStations(path);
      await this.addCenterReachLabels(reachStations);
      if (this.componentType === "terrace") {
        await this.displayDrainsAndBlocks(drains, 'drains');
        await this.displayDrainsAndBlocks(blocks, 'blocks');
      }
      this.hub?.MapStore?.addStations();
    }
  }

  @action protected setUpSketchCapability(): void {
    const map = this._profileView?.map;
    if (map) {
      this.setSketchLayer();
      this.mainSketchVm = new SketchViewModel({
        layer: this.sketchLayer,
        view: this._profileView!,
      });

      this.setEditProfile();

      this.mainSketchVm.on('create', (event) => this.handleMainSketchVmCreateEvent(event));
      this.mainSketchVm.on('update', (event) => this.handleMainSketchVmUpdateEvent(event));
    }
  }

  private setEditProfile() {
    const layer = 8;
    if (!layer) {
      if (this.mainSketchVm?.state !== 'ready') this.mainSketchVm?.complete();
      this.sketchLayer = void 0;
    } else if (this.mainSketchVm?.layer !== this.sketchLayer) {
      if (this.mainSketchVm?.state !== 'ready') this.mainSketchVm?.complete();
      this.sketchLayer = this.profiles[ProfileKeyCode[layer]];
      this.initSketchVm();
    }
  }

  protected setSketchLayer(): void {
    const id = `${this.componentType}_sketch`;
    const title = `${this.componentType} edited`;

    if (!this.sketchLayer) {
      const layer = this._profileView?.map.findLayerById(id);
      if (layer) {
        this.sketchLayer = layer as GraphicsLayer;
      } else {
        this.sketchLayer = new GraphicsLayer({ id, title });
        this._profileView?.map.add(this.sketchLayer);
      }
    }
  }

  private handleMainSketchVmCreateEvent(event: __esri.SketchViewModelCreateEvent): void {
    if (event.tool === 'polyline') {
      this.handlePolylineCreateEvent(event);
    } else if (event.tool === 'point') {
      this.handlePointCreateEvent(event);
    }
  }

  private handleMainSketchVmUpdateEvent(event: __esri.SketchViewModelUpdateEvent): void {
    if (event.tool === 'reshape') {
      this.handleReshapeEvent(event);
    }
  }

  public checkSketch(): boolean {
    if (this.sketchLayer && this.sketchLayer.graphics && this.sketchLayer.graphics.length > 0) {
      return true;
    } else {
      return false;
    }
  }

  protected removeReachStationLayer(): void {
    const reachStationsLayer = this._profileView?.map.findLayerById('reachStations');
    if (reachStationsLayer) this._profileView?.map.remove(reachStationsLayer);
    reachStationsLayer?.destroy();
    this.terraceBlocksLayer?.removeAll();
    this.terraceDrainsLayer?.removeAll();
    // clear reach stations on the map object
    if (this && this.hub && this.hub.MapStore && (this.hub.MapStore instanceof DynamicTerraceMapStore)) {
      this.hub.MapStore.clearReachStations();
    }
  }

  protected isValidLastSegment(line: Polyline): boolean {
    let isValid = true;

    const path = line.paths[0];
    const length = path.length;

    // check loops
    if (path.length >= 2) {
      const checkerPoint = line.getPoint(0, length - 1);
      for (let index = 0; index < length - 2; index++) {
        const checkeePoint = line.getPoint(0, index);
        if (esriGeometryEngine.distance(checkerPoint, checkeePoint) < this._loopThreshold) {
          isValid = false;
          this._isValidLine = false;
          break;
        }
      }
    }

    // check self-crossing
    if (isValid && path.length >= 3) {
      const spatialReference = line.spatialReference;
      const lastSegment = new Polyline({ paths: [[path[length - 2], path[length - 1]]], spatialReference });

      for (let index = 0; index < length - 3; index++) {
        const pntArr1 = path[index];
        const pntArr2 = path[index + 1];
        const currentSegment = new Polyline({ paths: [[pntArr1, pntArr2]], spatialReference });
        if (esriGeometryEngine.crosses(currentSegment, lastSegment)) {
          isValid = false;
          this._isValidLine = false;
          break;
        }
      }
    }

    return isValid;
  }

  protected async getProfileData(lowBank, bankOffset, sampleInterval, verticalInterval, alignedDesignElementId) {
    const params: ProfileRequestDTO = {
      alignedElementDesignParametersId: alignedDesignElementId,
      lowBank: lowBank,
      offsetValue: bankOffset,
      sampleInterval: sampleInterval,
      verticalInterval: verticalInterval
    }
    const response = await ProfileController.generateProfileUsingPOST(params);

    return response;
  }

  protected async getMaxMinYValues(profileList: any[], alignmentCenterLine) {
    // NOTE: profileList[0] should be centerlineProfileStations
    let ymin: number = 0;
    let ymax: number = 0;
    if (profileList[0].length > 0) {
      ymin = profileList[0][0].inputPoint.z;
      ymax = profileList[0][0].inputPoint.z;
    }

    profileList.forEach((profile, index) => {
      if (profile && profile.length) {
        profile.forEach(station => {
          if (index === 0 && station.inputPoint !== null && station.inputPoint !== undefined) {
            alignmentCenterLine.push([station.distance || 0, station.inputPoint.z || 0]);
          }
          const y = station.inputPoint.z;
          if (y < ymin) {
            ymin = y;
          } else if (y > ymax) {
            ymax = y;
          }
        });
      }
    });

    // get Reaches ymax and min
    if (this.componentType === 'waterway') {
      const stations = waterwayDesignStore.waterwayDesignObject.waterwayDesignStations;
      if (stations.length > 0) {
        if (stations[1].channelZ < ymin) {
          ymin = stations[1].channelZ;
        } else if (stations[1].channelZ > ymax) {
          ymax = stations[1].channelZ;
        }
        if (stations[stations.length - 2].channelZ < ymin) {
          ymin = stations[stations.length - 2].channelZ;
        } else if (stations[stations.length - 2].channelZ > ymax) {
          ymax = stations[stations.length - 2].channelZ;
        }
      }
    } else if (this.componentType === 'terrace') {
      const stations = terraceDesignStore.terraceDesignObject.terraceDesignStations;
      stations.forEach(station => {
        const y = station.channelElev;
        if (y > 0) {
          if (y < ymin) {
            ymin = y;
          } else if (y > ymax) {
            ymax = y;
          }
        }
      });
      if (terraceDesignStore.constructedProfileStations?.length) {
        terraceDesignStore.constructedProfileStations.forEach(station => {
          const y = station.inputPoint.z;
          if (y > 0) {
            if (y < ymin) {
              ymin = y;
            } else if (y > ymax) {
              ymax = y;
            }
          }
        });
      }
      if (terraceDesignStore.settledProfileStations?.length) {
        terraceDesignStore.settledProfileStations.forEach(station => {
          const y = station.inputPoint.z;
          if (y > 0) {
            if (y < ymin) {
              ymin = y;
            } else if (y > ymax) {
              ymax = y;
            }
          }
        });
      }
    }

    return { ymax, ymin }
  }

  async redrawMappingProfiles(fromAlignmentView: boolean = false) {
    this.maxXmin = 10000000;
    this.maxXmax = -1000;
    this.maxYmin = 10000000;
    this.maxYmax = -1000;
    designStore.commonHorizontalStretchFactor = undefined;
    designStore.commonVerticalStretchFactor = undefined;

    this.profileKeys.forEach((keyCode) => {
      if (keyCode) {
        const profile = this.profiles[ProfileKeyCode[keyCode]] as DynamicProfileClass;
        profile?.softProfileReset();
      }
    })

    if (this.componentType === 'terrace' || this.componentType === 'UGO') {
      this._centerlineStations = [...designStore.alignmentProfileStations];
    }

    // Get ymax and ymin
    let yValues;
    if (this._centerlineStations) {
      yValues = await this.getMaxMinYValues([this._centerlineStations, this._lowBankStations, this._rightBankStations, this._leftBankStations], this.alignmentCenterLine);

      // Draw bank(s)
      if (this._lowBankStations) {
        const lowBankProfile = this.profiles[ProfileKeyCode[5]] as DynamicProfileClass;
        lowBankProfile.ymax = yValues.ymax ? yValues.ymax : undefined;
        lowBankProfile.ymin = yValues.ymin ? yValues.ymin : undefined;
        const totalDist = this._lowBankStations[this._lowBankStations.length - 1].distance;
        if (totalDist) lowBankProfile.drawProfileGraphics(totalDist);
      }
      if (this._rightBankStations) {
        const rightBankProfile = this.profiles[ProfileKeyCode[6]] as DynamicProfileClass;
        rightBankProfile.ymax = yValues.ymax ? yValues.ymax : undefined;
        rightBankProfile.ymin = yValues.ymin ? yValues.ymin : undefined;
        const totalDist = this._rightBankStations[this._rightBankStations.length - 1].distance;
        if (totalDist) rightBankProfile.drawProfileGraphics(totalDist);
      }
      if (this._leftBankStations) {
        const leftBankProfile = this.profiles[ProfileKeyCode[7]] as DynamicProfileClass;
        leftBankProfile.ymax = yValues.ymax ? yValues.ymax : undefined;
        leftBankProfile.ymin = yValues.ymin ? yValues.ymin : undefined;
        const totalDist = this._leftBankStations[this._leftBankStations.length - 1].distance;
        if (totalDist) leftBankProfile.drawProfileGraphics(totalDist);
      }

      // Centerline
      const keyCode = (this.componentType === "terrace" || this.componentType === "UGO") ? 10 : 4;
      const centerlineProfile = this.profiles[ProfileKeyCode[keyCode]] as DynamicProfileClass;
      centerlineProfile.ymax = yValues.ymax ? yValues.ymax : undefined;
      centerlineProfile.ymin = yValues.ymin ? yValues.ymin : undefined;
      const totalDist = this._centerlineStations.length > 1 ? this._centerlineStations[this._centerlineStations.length - 1].distance : 0;
      if (this.componentType === 'terrace' || this.componentType === 'UGO' || (this.componentType === 'waterway' && centerlineProfile.groundProfilePoints.length === 0)) {
        centerlineProfile.mapGroundProfilePoints(this._centerlineStations);
      }
      if (totalDist) centerlineProfile.drawProfileGraphics(totalDist);
      if (fromAlignmentView) this.setUpTerraceAlignmentView(this._centerlineStations, designStore.commonHorizontalStretchFactor, designStore.commonVerticalStretchFactor);
      if (this.componentType === "terrace" && centerlineProfile.groundProfileLayer.graphics.length > 0) {
        await new Promise<void>((resolve) => setTimeout(() => {
          this._groundSurfaceProfileline = centerlineProfile.groundProfileLayer.graphics.getItemAt(0).geometry as Polyline;
          resolve();
        }, 1000));
      }

      if (this.componentType === "waterway") await this.drawGuardrail();
      if (this.componentType === "terrace") {
        if (this.checkSecondaryLayerStatus()) await this.drawTerraceStructures();
        this.setUpSketchCapability();
      }
    }
  }

  async generateReachLineFromStations(paths: any) {
    this.removeSketch();
    const polyline = new Polyline({
      paths: [paths],
      spatialReference: this.spatialReference
    });

    const symbol = this.componentType === 'waterway' ? reachLineSymbol.designLineSymbol : channelLineSymbol.designLineSymbol;
    // Create a polyline graphic
    const polylineGraphic = new Graphic({
      geometry: polyline,
      symbol: symbol
    });

    this.setSketchLayer();
    this.sketchLayer?.graphics.add(polylineGraphic);
    this.initSketchVm();

    this.generateReachStationLayer();
  }

  protected generateReachStationLayer(removeStartEndStations: boolean = false): void {
    this.removeReachStationLayer();
    if (this._profileView && this.mainSketchVm?.layer && this.mainSketchVm.layer.graphics && this.mainSketchVm.layer.graphics.length > 0) {
      const line = this.mainSketchVm?.layer.graphics.getItemAt(0).geometry as Polyline;
      const path = line.paths[0];

      if (this.componentType === 'waterway') {
        // Remove START and END for move/insert/delete operations
        if (removeStartEndStations) {
          if (waterwayDesignStore.waterwayDesignObject.startGradeLength > 0) path.shift();
          if (waterwayDesignStore.waterwayDesignObject.endGradeLength > 0) path.pop();
        }
      }

      const length = path.length;
      const pointSource = [] as Graphic[];
      const xScale = designStore.commonHorizontalStretchFactor ? designStore.commonHorizontalStretchFactor : 1;

      for (let i = 0; i < length; i++) {
        const stationLabel = formatStationString((path[i][0] / xScale).toFixed(1)).toString();
        const point = new Graphic({
          geometry: new Point({
            spatialReference: line.spatialReference,
            x: path[i][0],
            y: path[i][1],
          }),
          attributes: {
            ObjectID: i,
            Station: stationLabel
          },
        });
        pointSource.push(point);
      }

      const renderer = reachStationPointRenderer;
      const reachStations = new FeatureLayer({
        source: pointSource,
        fields: pointLabelFields,
        renderer: renderer as any,
        id: 'reachStations',
        objectIdField: 'ObjectID',
        displayField: 'Station',
        labelingInfo: [pointLabelClass]
      });
      this._profileView.map.add(reachStations);

      // populate reach stations on the map object
      if (this && this.hub && this.hub.MapStore && (this.hub.MapStore instanceof DynamicTerraceMapStore)) {
        this.hub.MapStore.setReachStations();
      }
    }
  }

  // ******* Terrace and Alignment View common functions ********
  protected get terraceBlocksLayer() {
    const id = 'terrace_blocks';
    const title = 'Terrace Blocks';

    if (!this._terraceBlocksLayer) {
      const layer = this._profileView?.map.findLayerById(id);
      if (layer) {
        this._terraceBlocksLayer = layer as GraphicsLayer;
      } else {
        this._terraceBlocksLayer = new GraphicsLayer({ id, title });
        this._profileView?.map.add(this._terraceBlocksLayer, 10);
      }
    }
    return this._terraceBlocksLayer;
  }

  protected get terraceDrainsLayer() {
    const id = 'terrace_drains';
    const title = 'Terrace Drains';

    if (!this._terraceDrainsLayer) {
      const layer = this._profileView?.map.findLayerById(id);
      if (layer) {
        this._terraceDrainsLayer = layer as GraphicsLayer;
      } else {
        this._terraceDrainsLayer = new GraphicsLayer({ id, title });
        this._profileView?.map.add(this._terraceDrainsLayer, 10);
      }
    }
    return this._terraceDrainsLayer;
  }

  public get waterLevelLayer() {
    const id = 'terrace_water_level';
    const title = 'Terrace Water Level';

    if (!this._waterLevelLayer) {
      const layer = this._profileView?.map.findLayerById(id);
      if (layer) {
        this._waterLevelLayer = layer as GraphicsLayer;
      } else {
        this._waterLevelLayer = new GraphicsLayer({ id, title });
        this._profileView?.map.add(this._waterLevelLayer, 1);
      }
    }
    return this._waterLevelLayer;
  }

  public get constructedTerraceLayer() {
    const id = 'constructed_terrace';
    const title = 'Constructed Terrace';

    if (!this._constructedTerraceLayer) {
      const layer = this._profileView?.map.findLayerById(id);
      if (layer) {
        this._constructedTerraceLayer = layer as GraphicsLayer;
      } else {
        this._constructedTerraceLayer = new GraphicsLayer({ id, title });
        this._profileView?.map.add(this._constructedTerraceLayer);
      }
    }
    return this._constructedTerraceLayer;
  }

  public get settledTerraceLayer() {
    const id = 'settled_terrace';
    const title = 'settled Terrace';

    if (!this._settledTerraceLayer) {
      const layer = this._profileView?.map.findLayerById(id);
      if (layer) {
        this._settledTerraceLayer = layer as GraphicsLayer;
      } else {
        this._settledTerraceLayer = new GraphicsLayer({ id, title });
        this._profileView?.map.add(this._settledTerraceLayer);
      }
    }
    return this._settledTerraceLayer;
  }

  async updateTerraceProfiles(profileData: any) {
    if (profileData) {
      if (profileData.constructedTerrace) {
        terraceDesignStore.constructedProfileStations = profileData.constructedTerrace.stationDTOList;
      }
      if (profileData.settledTerrace) {
        terraceDesignStore.settledProfileStations = profileData.settledTerrace.stationDTOList;
      }
      if (profileData.waterLevel) {
        terraceDesignStore.waterLevelData = profileData.waterLevel.stationDTOList;
      }
    }

    this.waterLevelLayer?.removeAll();
    this.constructedTerraceLayer?.removeAll();
    this.settledTerraceLayer?.removeAll();
    designStore.setTerraceSecLayerVisibility = true;

  }

  async generateTerraceProfile(sampleInterval, verticalInterval, alignedDesignElementId, fromAlignmentView: boolean = false) {
    this.profileKeys.forEach((keyCode) => {
      this.reset(keyCode);
    });
    designStore.commonHorizontalStretchFactor = undefined;
    designStore.commonVerticalStretchFactor = undefined;
    const payload = {
      alignedElementDesignParametersId: alignedDesignElementId,
      sampleInterval: sampleInterval,
      verticalInterval: verticalInterval
    }

    try {
      this.raiseStartLengthyProcessEvent(true);
      await doEvents();

      const profiles = await TerraceProfileController.createTerraceProfileUsingPOST(payload);
      await this.updateTerraceProfiles(profiles.data);
      if (profiles) {
        // Get ymax and ymin
        const groundSurfaceProfileStations = profiles.data.groundSurface?.stationDTOList;
        let yValues;
        if (groundSurfaceProfileStations?.length) {
          yValues = await this.getMaxMinYValues([groundSurfaceProfileStations], this.alignmentCenterLine);
          this._centerlineStations = designStore.alignmentProfileStations = [...groundSurfaceProfileStations];

          // Draw ground surface
          const groundSurfaceProfile = this.profiles[ProfileKeyCode[10]] as DynamicProfileClass;
          groundSurfaceProfile.ymax = yValues.ymax ? yValues.ymax : undefined;
          groundSurfaceProfile.ymin = yValues.ymin ? yValues.ymin : undefined;
          groundSurfaceProfile.mapGroundProfilePoints(groundSurfaceProfileStations);
          const totalDist = groundSurfaceProfileStations[groundSurfaceProfileStations.length - 1].distance;
          if (totalDist) groundSurfaceProfile.drawProfileGraphics(totalDist);
          if (groundSurfaceProfile.groundProfileLayer.graphics.length > 0) {
            await new Promise<void>(resolve => setTimeout(() => {
              this._groundSurfaceProfileline = groundSurfaceProfile.groundProfileLayer.graphics.getItemAt(0).geometry as Polyline;
              resolve();
            }, 1000));
          }
          if (fromAlignmentView) this.setUpTerraceAlignmentView(groundSurfaceProfileStations, designStore.commonHorizontalStretchFactor, designStore.commonVerticalStretchFactor);

          // set the alignment data for map location interpolation
          if (this && this.hub && this.hub.MapStore && (this.hub.MapStore instanceof DynamicTerraceMapStore)) {
            await this.hub.MapStore.setAlignment(groundSurfaceProfileStations);
            this.mapAlignmentSet = true;
          }
        }
        if (this.checkSecondaryLayerStatus()) await this.drawTerraceStructures();
      }
    } catch (error) {
      if ((error as any).response && (error as any).response.data && (error as any).response.data.errorMessage) {
        MessageCenter.addError((error as any).response.data.errorMessage, [], (error as any).response.data.originalErrorCode);
      }
    } finally {
      this.setUpSketchCapability();
      this.raiseStartLengthyProcessEvent(false);
    }
  }

  private drainSymbol = {
    type: "picture-marker" as const,
    url: DrainIcon,
    width: 20,
    height: 20,
    xoffset: 0,
    yoffset: 0
  };

  private blockSymbol = {
    type: "picture-marker" as const,
    url: BlockIcon,
    width: 20,
    height: 20,
    xoffset: 0,
    yoffset: 0
  };

  private async displayDrainsAndBlocks(paths, type: 'drains' | 'blocks') {
    const symbol = type === 'drains' ? this.drainSymbol : this.blockSymbol;
    const layer = type === 'drains' ? this.terraceDrainsLayer : this.terraceBlocksLayer;

    paths.forEach((point) => {
      const geom = new Point({
        x: point[0],
        y: point[1],
        spatialReference: this.spatialReference
      })

      const graphic = new Graphic({
        geometry: geom,
        symbol: symbol as any
      });

      layer?.add(graphic);
    });
    if (this._profileView) putStructureRepOnTop(this._profileView, layer.id);
  }

  async drawTerraceStructures() {
    this.resetSecondaryLayers();
    const xStretchFactor = designStore.commonHorizontalStretchFactor ? designStore.commonHorizontalStretchFactor : 1;
    const yStretchFactor = designStore.commonVerticalStretchFactor ? designStore.commonVerticalStretchFactor : AppConstants.profileStretchFactor;

    // Constructed Terrace
    if (terraceDesignStore.constructedProfileStations?.length) this.drawProfileDTOStructure(terraceDesignStore.constructedProfileStations, xStretchFactor, yStretchFactor, this.constructedTerraceLayer);

    // Settled Terrace
    if (terraceDesignStore.settledProfileStations?.length) this.drawProfileDTOStructure(terraceDesignStore.settledProfileStations, xStretchFactor, yStretchFactor, this.settledTerraceLayer);

    // Water Level
    if (terraceDesignStore.waterLevelData?.length) {
      const path = [
        [terraceDesignStore.waterLevelData[0].distance * xStretchFactor, terraceDesignStore.waterLevelData[0].inputPoint.z * yStretchFactor],
        [terraceDesignStore.waterLevelData[terraceDesignStore.waterLevelData.length - 1].distance * xStretchFactor, terraceDesignStore.waterLevelData[terraceDesignStore.waterLevelData.length - 1].inputPoint.z * yStretchFactor]
      ];

      if (path.length) {
        const polyline = new Polyline({
          paths: [path],
          spatialReference: this.spatialReference
        });

        const polylineGraphic = new Graphic({
          geometry: polyline,
          symbol: channelLineSymbol.waterLevelSymbol
        });

        this.waterLevelLayer.add(polylineGraphic);

      }
    }
    if (this._profileView) putStructureRepOnTop(this._profileView, 'terrace_sketch');
  }

  private drawProfileDTOStructure(data, xStretchFactor: number, yStretchFactor: number, layer) {
    const path: [number, number][] = [];
    data.forEach((station) => {
      path.push([station.distance * xStretchFactor, station.inputPoint.z * yStretchFactor]);
    });

    if (path.length) {
      const polyline = new Polyline({
        paths: [path],
        spatialReference: this.spatialReference
      });
      const lineSymbol = layer === this.constructedTerraceLayer ? reachLineSymbol.designLineSymbol : channelLineSymbol.settledTerraceSymbol;
      const polylineGraphic = new Graphic({
        geometry: polyline,
        symbol: lineSymbol
      });

      layer.add(polylineGraphic);
    }
  }

  public async addCenterReachLabels(stations: any[]) {
    this.removeMidPointStationLayer();
    if (this._profileView && this.mainSketchVm?.layer && this.mainSketchVm.layer.graphics && this.mainSketchVm.layer.graphics.length > 0) {
      const line = this.mainSketchVm.layer.graphics.getItemAt(0).geometry as Polyline;
      const midPointSource = [] as Graphic[];
      const startEndMidPointSource = [] as Graphic[];
      stations.forEach((station, index) => {
        if ((index > 0 && index < (stations.length - 2)) || (index === 0 && this.getGroundElevation(station) > 0) || (index === stations.length - 2 && this.getGroundElevation(stations[index + 1]) > 0)) {
          const stationValue = station.endStation;
          const reachName = stations[index + 1].reachId;

          //start point of a reach
          const xValue = this.getXStretchFactor() ? parseStationString(stationValue) * this.getXStretchFactor() : parseStationString(stationValue);
          const yValue = this.getChannelElevation(station) * this.getYStretchFactor();
          const startPoint = new Point({ x: xValue, y: yValue });

          //end point of a reach
          const NextStationXvalue = this.getXStretchFactor() ? parseStationString(stations[index + 1].endStation) * this.getXStretchFactor() : parseStationString(stations[index + 1].endStation);
          const NextStationYvalue = this.getChannelElevation(stations[index + 1]) * this.getYStretchFactor();
          const endPoint = new Point({ x: NextStationXvalue, y: NextStationYvalue });

          const midPoint = this.calculateMidPoint(startPoint, endPoint);
          const slope = Number(stations[index + 1].grade) * 100;
          const midPointLabel = reachName + ", " + slope.toFixed(2) + "%";

          const midPointGraphic = new Graphic({
            geometry: new Point({ spatialReference: line.spatialReference, x: midPoint[0], y: midPoint[1] }),
            attributes: {
              ObjectID: index,
              Station: midPointLabel
            }
          });

          if (index < (stations.length - 1)) {
            midPointSource.push(midPointGraphic);
          } else {
            startEndMidPointSource.push(midPointGraphic);
          }
        }
      });

      const reachLabelFeatureLayer = new FeatureLayer({
        source: midPointSource,
        fields: pointLabelFields,
        objectIdField: "ObjectID",
        id: "reachLabelFeatureLayer",
        displayField: "Station",
        labelingInfo: [pointLabelClass],
        renderer: invisibleRenderer as any,
      });
      this._profileView?.map.add(reachLabelFeatureLayer);

      pointLabelClass2.minScale = this._profileView?.scale * 0.3; // show START and END reach labels at or above 70% zoom
      if (startEndMidPointSource.length > 0) {
        const startEndLabelFeatureLayer = new FeatureLayer({
          source: startEndMidPointSource,
          fields: pointLabelFields,
          objectIdField: "ObjectID",
          id: "startEndLabelFeatureLayer",
          displayField: "Station",
          labelingInfo: [pointLabelClass2],
          renderer: invisibleRenderer as any
        });
        this._profileView?.map.add(startEndLabelFeatureLayer);
      }
    }
  }
  private getChannelElevation(station: any): any {
    let value;
    switch (this.componentType) {
      case 'waterway':
        value = station.channelZ;
        break;
      case 'terrace':
        value = station.channelElev;
        break;
      case 'UGO':
        value = station.pipeFlowElev;
        break;
    }
    return value;
  }

  private getGroundElevation(station: any): any {
    let value;
    switch (this.componentType) {
      case 'waterway':
        value = station.groundZ;
        break;
      case 'terrace':
        value = station.groundElev;
        break;
      case 'UGO':
        value = station.groundElev;
        break;
    }
    return value;
  }
  protected removeMidPointStationLayer(): void {
    const reachLabelFeatureLayer = this._profileView?.map.findLayerById('reachLabelFeatureLayer');
    const startEndLabelFeatureLayer = this._profileView?.map.findLayerById('startEndLabelFeatureLayer');
    if (reachLabelFeatureLayer) this._profileView?.map.remove(reachLabelFeatureLayer);
    if (startEndLabelFeatureLayer) this._profileView?.map.remove(startEndLabelFeatureLayer);
    reachLabelFeatureLayer?.destroy();
    startEndLabelFeatureLayer?.destroy();
  }

  protected calculateMidPoint(startPoint: Point, endPoint: Point): number[] {
    return [(startPoint.x + endPoint.x) / 2, (startPoint.y + endPoint.y) / 2];
  }

  resetSecondaryLayers() {
    this.waterLevelLayer.removeAll();
    this.constructedTerraceLayer.removeAll();
    this.settledTerraceLayer.removeAll();
  }

  checkSecondaryLayerStatus() {
    if (this.waterLevelLayer.visible === false || this.constructedTerraceLayer.visible === false || this.settledTerraceLayer.visible === false) {
      return false;
    }
    return true;
  }

  toggleSecondaryLayersVisibility(visibility: boolean) {
    if (this.waterLevelLayer) this.waterLevelLayer.visible = visibility;
    if (this.constructedTerraceLayer) this.constructedTerraceLayer.visible = visibility;
    if (this.settledTerraceLayer) this.settledTerraceLayer.visible = visibility;
  }

  public async updateTableOnChangeStationValue(dataPoint: XyzPoint, index: number): Promise<void> {
    const stations = terraceDesignStore.terraceDesignObject.terraceDesignStations;

    // Update status
    stations[index].status = 'IN PROGRESS';

    // Update endStation
    stations[index].endStation = formatStationString(dataPoint.getX().toFixed(1));

    // Update channelElev
    stations[index].channelElev = parseFloat(dataPoint.getY().toFixed(1));

    // Update startStation for the next station if it exists
    if (index + 1 < stations.length) {
      stations[index + 1].startStation = formatStationString(dataPoint.getX().toFixed(1));
    }

    // Prepare payload for fetching ground elevation
    const payload = {
      alignedElementDesignParametersId: terraceDesignStore.terraceDesignObject.alignedElementDesignParametersId,
      reachStationDistance: parseFloat(dataPoint.getX().toFixed(1)),
    };
    const response = await ProfileController.getGroundElevationByReachStationDistanceFromAlignmentUsingPOST(payload);

    // Update ground elevation and cutFill
    if (response.data && response.data.groundElevation !== undefined) {
      stations[index].groundElev = parseFloat(response.data.groundElevation.toFixed(1));
      stations[index].cutFill = parseFloat((stations[index].channelElev - stations[index].groundElev).toFixed(1));
    }

    // Recalculate grades and distances for the current and adjacent stations
    const currentStation = stations[index];
    if (index > 0) {
      const prevStation = stations[index - 1];
      const deltaCurY = prevStation?.channelElev - currentStation.channelElev;
      const deltaCurX = parseStationString(prevStation?.endStation) - parseStationString(currentStation.endStation);
      if (deltaCurX !== 0) {
        currentStation.grade = deltaCurY / deltaCurX;
        currentStation.stationDist = Math.abs(deltaCurX);
      }
    }
    if (index + 1 < stations.length) {
      const nextStation = stations[index + 1];
      const deltaNextY = nextStation?.channelElev - currentStation.channelElev;
      const deltaNextX = parseStationString(nextStation?.endStation) - parseStationString(currentStation.endStation);
      if (deltaNextX !== 0) {
        nextStation.grade = deltaNextY / deltaNextX;
        nextStation.stationDist = Math.abs(deltaNextX);
      }
    }
    // Persist the updated stations back to the store
    terraceDesignStore.terraceDesignObject.terraceDesignStations = stations;
  }
  // ******* End of Profile Mapping common functions ********


  /** events */
  public startLengthyProcess: ((state: boolean) => void) | undefined = undefined;
  public disableButton: ((state: boolean) => void) | undefined = undefined;

  public raiseStartLengthyProcessEvent(state: boolean) {
    if (this.startLengthyProcess) this.startLengthyProcess(state);
  }

  public disableButtonEvent(state: boolean) {
    if (this.disableButton) this.disableButton(state);
  }

  /** data operations */
  LoadProfile(tableObject: any, keyCode: ProfileKeyCode) {
    this.profileData[ProfileKeyCode[keyCode]] = tableObject;
  }

  // #temp-fix start
  public drawOneTimeGroundProfile(userPoints: any[], block, beginningStation) {
    this.reset(1);
    if (!block) {
      const keyCode = this.componentType === "terrace" ? 10 : 1;
      const profile = this.profiles[ProfileKeyCode[keyCode]] as DynamicProfileClass;
      if (this.componentType === 'alignment') {
        profile.drawOneTimeGroundProfile(userPoints, beginningStation);
      } else {
        this._centerlineStations = designStore.alignmentProfileStations;
        this.redrawMappingProfiles(true);
      }
    }
  }
  // #temp-fxi end


  /** public methods */
  // communication pattern 2: delegation pattern
  sketchPolyline() {
    this.hub?.SketchPolyline();
  }

  onceMapViewCreated() {
    return this._profileView?.when();
  }

  drawCurvedProfile(curveData: any, keyCode: ProfileKeyCode) {
    const profile = this.profiles[ProfileKeyCode[keyCode]] as DynamicProfileClass;
    profile?.drawCurvedGroundProfile(curveData);
    this.hub?.MapStore?.generateAlignmentLabelsLayer();
  }

  protected async initDraw(profile: DynamicProfileClass, startDist?: number, shiftProfileValue?) {
    if (this.threeCrossSection && startDist) {
      this.threeCrossSectionStartDist = startDist - this.threeCrossSectionBufferWidth;
    }
    await profile.drawGroundProfile(shiftProfileValue);
  }

  private initData(profile: DynamicProfileClass, keyCode: ProfileKeyCode) {
    if (keyCode === 1 && designStore.currentSurface.surfaceDefClassName === 'dem') {
      if (this.profileData[ProfileKeyCode[keyCode]].alignmentStations && this.profileData[ProfileKeyCode[keyCode]].alignmentStations.length > 1) {
        const is2dAlignment = this.profileData[ProfileKeyCode[keyCode]].alignmentStations.some(station => station.z == 0);
        if (!is2dAlignment) {
          if (this.hub && this.hub.MapStore) {
            this.profileData[ProfileKeyCode[keyCode]].alignmentStations.forEach((station) => {
              const xyzPoint = new XyzPoint(station.x, station.y, station.z);
              this.hub?.MapStore?.userActivePoints.push(xyzPoint);
            });
            this.hub.MapStore.getContourIntersects(false);
            profile.mapDesignVertices(this.hub.MapStore.activeProfilePoints);
          }
        }
      }
    } else {
      profile.initData(this.profileData[ProfileKeyCode[keyCode]])
    }
  }

  protected abstract initSketchVm(): void;
  protected abstract removeSketch(): void;

  @action
  protected broadcastSketchState(state): void {
    this.hub?.NextMapSketchState(state);
  }

  @action
  protected broadcastNewVertexInfo(
    info: any,
  ): void {
    this.hub?.NextNewProfileVertexInfo(info);
  }

  @action
  protected broadcastMoveVertexInfo(
    newVertex: XyzPoint,
    vertexIndex: number,
    addWDTEdits?: any): void {
    this.hub?.NextMoveProfileVertexInfo({ newVertex, vertexIndex, addWDTEdits });
  }
  @action
  protected broadcastInsertVertexInfo(
    newVertex: XyzPoint,
    vertexIndex: number,
    keyCode: ProfileKeyCode,
    addWDTEdits?: any): void {
    this.hub?.NextInsertProfileVertexInfo({ newVertex, vertexIndex, keyCode, addWDTEdits });
  }

  get TableStore() {
    return this.hub?.TableStore;
  }

  private set profileView(view: MapView) {
    this._profileView = view;
    this.maxExtent = view.extent;

    this.h_grid = this.initHorizontalGridLineLayer();
    this.v_grid = this.initVerticalGridLineLayer();

    // set up profileView ui
    view.ui.remove(['zoom', 'attribution']);


    // disable zoom/pan out
    view.watch('extent', (event) => {
      if (this.isZoomOffLimits()) {
        this._profileView!.extent = this.maxExtent!;
      }
    });
  }

  protected mapRound(input: number): number {
    return Math.round((input + Number.EPSILON) * 100) / 100;
  }

  protected spatialReference = new SpatialReference({ wkid: 102965 });

  private fields = [
    {
      name: 'ObjectID',
      alias: 'ObjectID',
      type: 'oid',
    },
    {
      name: 'Label',
      alias: 'Label',
      type: 'string',
    },
  ];

  private labelClass = {
    symbol: {
      type: 'text',

      color: 'gray',
      font: {
        family: 'Ubuntu Mono',
        size: 9,
      },
    },
    labelPlacement: 'center-along',
    labelExpressionInfo: {
      expression: '$feature.Label',
    },
    maxScale: 0,
    minScale: 25000000,
  };

  private labelClass_h = {
    symbol: {
      type: 'text',
      color: 'gray',
      font: {
        family: 'Ubuntu Mono',
        size: 9,
      },
    },
    labelPlacement: 'center-left',
    labelExpressionInfo: {
      expression: '$feature.Label',
    },
    maxScale: 0,
    minScale: 25000000,
    allowOverrun: true,
    deconflictionStrategy: 'none',
  };

  private labelClass_v = {
    symbol: {
      type: 'text',
      color: 'gray',
      font: {
        family: 'Ubuntu Mono',
        size: 9,
      },
    },
    labelPlacement: 'below-center',
    labelExpressionInfo: {
      expression: '$feature.Label',
    },
    maxScale: 0,
    minScale: 25000000,
    allowOverrun: true,
    deconflictionStrategy: 'none',
  };

  private profileGridLineRenderer = {
    type: 'simple',
    symbol: {
      type: 'simple-line',
      color: [10, 10, 10, 0.3],
      width: '0.5',
    },
  };

  private profileLabelPointRenderer = {
    type: 'simple',
    symbol: {
      type: 'simple-marker',
      color: 'transparent',
      size: 0,
    },
  };

  private profileGuardrailRenderer = {
    type: 'simple',
    symbol: {
      type: 'simple-line',
      color: netsLegendDarkRed,
      width: '2',
      style: 'short-dash',
    },
  };

  /** private methods */
  private isZoomOffLimits() {
    const extent = this._profileView!.extent;
    if (!extent) return false;
    if (
      this.maxExtent &&
      extent.xmin < this.maxExtent.xmin &&
      extent.xmax > this.maxExtent.xmax &&
      extent.ymin < this.maxExtent.ymin &&
      extent.ymax > this.maxExtent.ymax
    ) {
      return true;
    }
    return false;
  }

  /** actions */
  @action
  private async insertVertex(newVertex: XyzPoint, vertexIndex: number, keyCode: ProfileKeyCode, blockModifyProfile, alignmentObject?) {
    /*Reset profile max extent */
    this.maxXmin = 10000000;
    this.maxXmax = -1000;
    this.maxYmin = 10000000;
    this.maxYmax = -1000;

    if (blockModifyProfile) {
      this.reset(keyCode);
    } else {
      const profile = this.profiles[ProfileKeyCode[keyCode]] as DynamicProfileClass;
      await profile?.insertVertex(newVertex, vertexIndex, alignmentObject);
    }
  }

  @action
  private async modifyVertex(newVertex: any, vertexIndex: any, keyCode: ProfileKeyCode, blockModifyProfile, alignmentObject?) {
    if (blockModifyProfile) {
      this.reset(keyCode);
    } else {
      this.reset(keyCode);
      const profile = this.profiles[ProfileKeyCode[keyCode]] as DynamicProfileClass;
      await profile?.modifyVertex(newVertex, vertexIndex, alignmentObject);
    }
  }

  @action
  private async deleteVertex(info: any) {
    if (info.blockModifyProfile) {
      this.reset(info.keyCode);
    } else {
      const profile = this.profiles[ProfileKeyCode[info.keyCode]] as DynamicProfileClass;
      await profile?.deleteVertex(info);
    }
  }

  public reset(keyCode: ProfileKeyCode | undefined) {
    if (keyCode) {
      const profile = this.profiles[ProfileKeyCode[keyCode]] as DynamicProfileClass;
      if (keyCode !== 8) {
        this.maxXmin = 10000000;
        this.maxXmax = -1000;
        this.maxYmin = 10000000;
        this.maxYmax = -1000;
      }
      profile?.reset();
      if (keyCode === 2) {
        if (this.profiles[ProfileKeyCode[3]]) { // if key code is original cross section profile, check if design cross section profile also exists
          const profile = this.profiles[ProfileKeyCode[3]] as DynamicProfileClass;
          profile?.reset();
        }
      }
    }
  }

  async interpolateSurfaceNewVertex(dataZ: number, dataX: number, dataY: number, keyCode: ProfileKeyCode, blockProfileForTable?, blockProfile2dAlignment?) {
    if (blockProfile2dAlignment) {
      this.reset(keyCode);
    } else {
      if (!blockProfileForTable) {
        const profile = this.profiles[ProfileKeyCode[keyCode]] as DynamicProfileClass;
        await profile.interpolateSurfaceNewVertex(dataZ, dataX, dataY, keyCode);
      }
    }
  }

  private async updateGridLineLayers(maxExtent?: Extent, keyCode?: number) {
    if (maxExtent) {
      this.x_padding = Math.floor(maxExtent.xmax / 10);
      this.y_padding = Math.floor((maxExtent.ymax - maxExtent.ymin) / 10);

      if (this.componentType === 'waterway' || this.threeCrossSection || this.componentType === 'terrace' || this.componentType === 'UGO') {
        this.maxXmin = this.componentType === 'waterway' || this.componentType === 'terrace' || this.componentType === 'UGO' ? maxExtent.xmin : this.threeCrossSectionStartDist;
        this.maxXmax = this.threeCrossSection ? (this.threeCrossSectionEndDist) : maxExtent.xmax;
        this.maxYmin = maxExtent.ymin - this.y_padding < this.maxYmin ? maxExtent.ymin - this.y_padding : this.maxYmin;
        this.maxYmax = maxExtent.ymax > this.maxYmax ? maxExtent.ymax : this.maxYmax;
      } else {
        this.maxXmin = maxExtent.xmin - this.x_padding < this.maxXmin ? maxExtent.xmin - this.x_padding : this.maxXmin;
        this.maxXmax = maxExtent.xmax > this.maxXmax ? maxExtent.xmax : this.maxXmax;
        this.maxYmin = maxExtent.ymin < this.maxYmin ? maxExtent.ymin : this.maxYmin;
        this.maxYmax = maxExtent.ymax > this.maxYmax ? maxExtent.ymax : this.maxYmax;
      }

      this.maxExtent = new Extent({
        xmin: this.maxXmin,
        ymin: this.maxYmin,
        xmax: this.maxXmax,
        ymax: this.maxYmax,
        spatialReference: this._profileView?.spatialReference,
      });
      if ((designStore.autoProfileZoom && this.componentType === 'alignment') || this.componentType !== 'alignment') this._profileView!.extent = this.maxExtent!;
    }

    // delete the previous grid line layers
    if (!(this.threeCrossSection && keyCode === 2)) {
      this.cleanGridLayers();
    }

    if (!(this.threeCrossSection && keyCode === 2)) { // Do not draw grid line for three cross section original graphic
      // get the proper grid size
      const xGridSize = this.h_gridSize ? this.h_gridSize : this.getBestGridSize(this.maxXmax / 5, true);
      const yGridSize = this.v_gridSize ? this.v_gridSize : this.getBestGridSize((this.maxYmax - this.maxYmin) / 3, false);

      // // generate the grid line layers
      this.h_grid = this.getHorizontalGridLineLayer(this.spatialReference, this.fields, this.labelClass, xGridSize, yGridSize);
      this.v_grid = this.getVerticalGridLineLayer(this.spatialReference, this.fields, this.labelClass, xGridSize, yGridSize);
      this._profileView!.map.addMany([this.h_grid, this.v_grid]);

      // generate the label point layers for waterway
      if (this.componentType === 'waterway' || this.threeCrossSection || this.componentType === 'terrace' || this.componentType === 'UGO') {
        this.h_label = this.getHorizontalLabelPointLayer(this.spatialReference, this.fields, this.labelClass_h, yGridSize);
        this.v_label = this.getVerticalLabelPointLayer(this.spatialReference, this.fields, this.labelClass_v, xGridSize);
        this._profileView!.map.addMany([this.h_label, this.v_label]);
      }
    }
  }

  updateUserGridValues(gridValue: number, updateLines: boolean = true) {
    let changed = false;
    if (this.h_gridSize != gridValue) {
      this.h_gridSize = gridValue;
      changed = true;
    }
    if (this.v_gridSize != gridValue) {
      this.v_gridSize = gridValue;
      changed = true;
    }
    if (changed && updateLines) this.updateGridLineLayers();
  }

  protected getXStretchFactor() {
    let xStretchFactor;
    switch (true) {
      case this.componentType === 'waterway' || this.componentType === 'terrace' || this.componentType === 'UGO':
        xStretchFactor = designStore.commonHorizontalStretchFactor;
        break;
      case this.componentType === 'threeCrossSection1':
        xStretchFactor = designStore.tcs1HorizontalStretchFactor;
        break;
      case this.componentType === 'threeCrossSection2':
        xStretchFactor = designStore.tcs2HorizontalStretchFactor;
        break;
      case this.componentType === 'threeCrossSection3':
        xStretchFactor = designStore.tcs3HorizontalStretchFactor;
        break;
      default:
        xStretchFactor = 1;
    }
    return xStretchFactor;
  }

  private getYStretchFactor() {
    let yStretchFactor;
    switch (true) {
      case this.componentType === 'waterway' || this.componentType === 'terrace' || this.componentType === 'UGO':
        yStretchFactor = designStore.commonVerticalStretchFactor;
        break;
      default:
        yStretchFactor = AppConstants.profileStretchFactor;
    }
    return yStretchFactor;
  }

  private initHorizontalGridLineLayer() {
    const h_gridLineLayer = new FeatureLayer();

    return h_gridLineLayer;
  }

  private getHorizontalGridLineLayer(spatialReference: SpatialReference, fields, labelClass, xGridSize: number, yGridSize: number) {
    const xStretchFactor = this.getXStretchFactor();
    const yStretchFactor = this.getYStretchFactor();
    const xStart = this.threeCrossSection ? this.threeCrossSectionStartDist : 0;
    const xLength = (this.maxXmax / xStretchFactor) + xGridSize + Math.abs(xStart);
    const roundedXLength = Math.round(xLength / xGridSize) * xGridSize;
    const minY = (this.componentType === 'waterway' || this.threeCrossSection || this.componentType === 'terrace' || this.componentType === 'UGO') ? this.maxYmin + this.y_padding : this.maxYmin - (yGridSize * yStretchFactor) * 5;
    const maxY = this.maxYmax + (yGridSize * yStretchFactor) * 5;

    const h_lineSource = [] as Graphic[];
    let oid = 0;
    for (let i = minY; i <= maxY; i = i + (yGridSize * yStretchFactor)) {
      const h_lineGraphic = new Graphic({
        geometry: new Polyline({
          spatialReference,
          paths: [
            [
              [xStart * xStretchFactor, i],
              [roundedXLength * xStretchFactor, i],
            ],
          ],
        }),
        attributes: {
          ObjectID: oid,
          Label: !(this.componentType === 'waterway' || this.threeCrossSection || this.componentType === 'terrace' || this.componentType === 'UGO') ? `${(i / yStretchFactor).toFixed(2)}` : null,
        },
      });
      h_lineSource.push(h_lineGraphic);
      oid++;
    }

    const h_gridLineLayer = new FeatureLayer({
      source: h_lineSource,
      fields,
      renderer: this.profileGridLineRenderer as any,
      id: AppConstants.profileHorizontalGridLineLayerId,
      title: 'horizontal',
      objectIdField: 'ObjectID',
      labelingInfo: [labelClass] as any,
    });

    return h_gridLineLayer;
  }

  private initVerticalGridLineLayer() {
    const v_gridLineLayer = new FeatureLayer();
    return v_gridLineLayer;
  }

  private getVerticalGridLineLayer(spatialReference: SpatialReference, fields, labelClass, xGridSize: number, yGridSize: number) {
    const xStretchFactor = this.getXStretchFactor();
    const yStretchFactor = this.getYStretchFactor();
    const startX = this.threeCrossSection ? this.threeCrossSectionStartDist + (-this.threeCrossSectionStartDist % xGridSize) : 0;
    const maxX = (this.maxXmax / xStretchFactor) + xGridSize + Math.abs(startX);
    const roundedMaxX = Math.round(maxX / xGridSize) * xGridSize;
    const minY = (this.componentType === 'waterway' || this.threeCrossSection || this.componentType === 'terrace' || this.componentType === 'UGO') ? this.maxYmin + this.y_padding : this.maxYmin - (yGridSize * yStretchFactor) * 5;
    const maxY = this.maxYmax + (yGridSize * yStretchFactor) * 5;

    this.guardrailMinY = minY;
    this.guardrailMaxY = maxY;

    const v_lineSource = [] as Graphic[];
    if (this.threeCrossSection && startX !== this.threeCrossSectionStartDist) {
      const h_lineGraphic = new Graphic({
        geometry: new Polyline({
          spatialReference,
          paths: [
            [
              [this.threeCrossSectionStartDist * xStretchFactor, minY],
              [this.threeCrossSectionStartDist * xStretchFactor, maxY],
            ],
          ],
        }),
        attributes: {
          ObjectID: 0
        },
      });
      v_lineSource.push(h_lineGraphic);
    }
    let oid = this.threeCrossSection ? 1 : 0;
    for (let i = startX; i <= roundedMaxX; i = i + xGridSize) {
      const scaledX = xStretchFactor ? i * xStretchFactor : i;
      const h_lineGraphic = new Graphic({
        geometry: new Polyline({
          spatialReference,
          paths: [
            [
              [scaledX, minY],
              [scaledX, maxY],
            ],
          ],
        }),
        attributes: {
          ObjectID: oid,
          Label: !(this.componentType === 'waterway' || this.threeCrossSection || this.componentType === 'terrace' || this.componentType === 'UGO') ? `${i}` : null,
        },
      });
      v_lineSource.push(h_lineGraphic);
      oid++;
    }

    const v_gridLineLayer = new FeatureLayer({
      source: v_lineSource,
      fields,
      renderer: this.profileGridLineRenderer as any,
      id: AppConstants.profileVerticalGridLineLayerId,
      title: 'vertical',
      objectIdField: 'ObjectID',
      labelingInfo: [labelClass] as any,
    });

    return v_gridLineLayer;
  }

  private getHorizontalLabelPointLayer(spatialReference: SpatialReference, fields, labelClass, yGridSize: number) {
    const yStretchFactor = this.getYStretchFactor();
    const minY = this.maxYmin + this.y_padding;
    const maxY = this.maxYmax + (yGridSize * yStretchFactor) * 5;
    const xStart = this.threeCrossSection ? this.threeCrossSectionStartDist * this.getXStretchFactor() : 0;

    const h_pointSource = [] as Graphic[];
    let oid = 0;
    for (let i = minY; i <= maxY; i = i + (yGridSize * yStretchFactor)) {
      const h_pointGraphic = new Graphic({
        geometry: new Point({
          spatialReference,
          x: xStart,
          y: i,
        }),
        attributes: {
          ObjectID: oid,
          Label: `${((i) / yStretchFactor).toFixed(1)}`,
        },
      });
      h_pointSource.push(h_pointGraphic);
      oid++;
    }

    const h_labelPointLayer = new FeatureLayer({
      source: h_pointSource,
      fields,
      renderer: this.profileLabelPointRenderer as any,
      id: 'profile_horizontal_label',
      title: 'horizontal',
      objectIdField: 'ObjectID',
      labelingInfo: [labelClass] as any,
    });

    return h_labelPointLayer;
  }

  private getVerticalLabelPointLayer(spatialReference: SpatialReference, fields, labelClass, xGridSize: number) {
    const xStretchFactor = this.getXStretchFactor();
    const startX = this.threeCrossSection ? this.threeCrossSectionStartDist + (-this.threeCrossSectionStartDist % xGridSize) : 0;
    const maxX = (this.maxXmax / xStretchFactor) + xGridSize + Math.abs(startX);
    const roundedMaxX = Math.round(maxX / xGridSize) * xGridSize;
    const minY = this.maxYmin + this.y_padding;

    const v_pointSource = [] as Graphic[];

    let oid = 0;
    for (let i = startX; i <= roundedMaxX; i = i + xGridSize) {
      const scaledX = xStretchFactor ? i * xStretchFactor : i;
      const v_pointGraphic = new Graphic({
        geometry: new Point({
          spatialReference,
          x: scaledX,
          y: minY,
        }),
        attributes: {
          ObjectID: oid,
          Label: this.threeCrossSection ? `${Number(i)}` : `${String(Number(i / 100).toFixed(2)).replace('.', '+')}`,
        },
      });
      v_pointSource.push(v_pointGraphic);
      oid++;
    }

    const v_labelPointLayer = new FeatureLayer({
      source: v_pointSource,
      fields,
      renderer: this.profileLabelPointRenderer as any,
      id: 'profile_vertical_label',
      title: 'vertical',
      objectIdField: 'ObjectID',
      labelingInfo: [labelClass] as any,
    });

    return v_labelPointLayer;
  }

  private getBestGridSize(maxSize, verticalGridLines: boolean) {
    let stretchFactor = verticalGridLines ? 1 : AppConstants.profileStretchFactor;
    if (this.componentType === 'waterway' || this.threeCrossSection || this.componentType === 'terrace' || this.componentType === 'UGO') {
      if (verticalGridLines) {
        stretchFactor = this.getXStretchFactor();
        if (this.threeCrossSection) maxSize = this.threeCrossSectionEndDist + Math.abs(this.threeCrossSectionStartDist);
      } else {
        stretchFactor = this.getYStretchFactor();
      }
    }
    const possibleGridSizes = [500, 200, 100, 50, 20, 10, 5, 2, 1];
    for (const size of possibleGridSizes) {
      if ((size * stretchFactor) <= maxSize) {
        return size;
      }
    }
    return 5;
  }

  cleanGridLayers() {
    if (this._profileView) {
      const layer_h = this._profileView.map.findLayerById(AppConstants.profileHorizontalGridLineLayerId);
      const layer_v = this._profileView.map.findLayerById(AppConstants.profileVerticalGridLineLayerId);
      if (layer_h) this._profileView.map.remove(layer_h);
      if (layer_v) this._profileView.map.remove(layer_v);
      if (this.componentType === 'waterway' || this.threeCrossSection || this.componentType === 'terrace' || this.componentType === 'UGO') {
        const point_h = this._profileView.map.findLayerById('profile_horizontal_label');
        const point_v = this._profileView.map.findLayerById('profile_vertical_label');
        if (point_h) this._profileView.map.remove(point_h);
        if (point_v) this._profileView.map.remove(point_v);
      }
    }
  }

  public async drawGuardrail() {
    // remove previous guardrails
    const guardrails = this._profileView!.map.findLayerById('profile_guardrail');
    if (guardrails) this._profileView!.map.remove(guardrails);

    let start = waterwayDesignStore.waterwayDesignObject.startGuardrail;
    let end = waterwayDesignStore.waterwayDesignObject.endGuardrail;

    if (this.componentType === 'waterway' && designStore.commonHorizontalStretchFactor) {
      start = start * designStore.commonHorizontalStretchFactor;
      end = end * designStore.commonHorizontalStretchFactor;
    }
    const lineSource = [] as Graphic[];
    const startX = start > 0 ? start : 0;
    const endX = end;

    const startLineGraphic = new Graphic({
      geometry: new Polyline({
        spatialReference: this.spatialReference,
        paths: [
          [
            [startX, this.guardrailMinY],
            [startX, this.guardrailMaxY],
          ],
        ],
      }),
      attributes: {
        ObjectID: 0,
      },
    });
    lineSource.push(startLineGraphic);

    const endLineGraphic = new Graphic({
      geometry: new Polyline({
        spatialReference: this.spatialReference,
        paths: [
          [
            [endX, this.guardrailMinY],
            [endX, this.guardrailMaxY],
          ],
        ],
      }),
      attributes: {
        ObjectID: 1,
      },
    });
    lineSource.push(endLineGraphic);

    const guardrailLineLayer = new FeatureLayer({
      source: lineSource,
      // fields: this.fields,
      renderer: this.profileGuardrailRenderer as any,
      id: 'profile_guardrail',
      title: 'guardrail',
      objectIdField: 'ObjectID',
    });

    this._profileView!.map.add(guardrailLineLayer);
  }

  public startCoordinateReadout() {
    if (this._profileView) {
      this._viewPointerMoveHandle = this._profileView.on('pointer-move', this.viewPointerCoordinateHandler);
      this._viewPointerLeaveHandle = this._profileView.on('pointer-leave', this.viewPointerLeaveHandler);
    }
  }

  public stopCoordinateReadout() {
    const popup = document.getElementById(`${this.componentType}-profile-coordinates-container`);
    if (popup) popup.style.visibility = 'hidden';
    this._viewPointerMoveHandle?.remove();
    this._viewPointerLeaveHandle?.remove();
  }

  private viewPointerLeaveHandler = async () => {
    const popup = document.getElementById(`${this.componentType}-profile-coordinates-container`);
    if (popup) popup.style.visibility = 'hidden';
  }

  private viewPointerCoordinateHandler = async (event: __esri.ViewPointerMoveEvent) => {
    const yStretchFactor = this.getYStretchFactor();
    if (this._profileView) {
      const movingPoint = this._profileView.toMap(event);
      const elevation = String(Number(movingPoint.y / yStretchFactor).toFixed(1));
      const distance = String((movingPoint.x / this.getXStretchFactor()).toFixed(1))
      let cutFill = '';
      if (this.componentType === "waterway") {
        const groundZ = this.getCenterlineZByDistance(distance);
        if (groundZ) {
          cutFill = (Number(elevation) - groundZ).toFixed(1);
          if (cutFill === '-0.0') {
            cutFill = '0.0'
          }
        }
      }
      const yPadding = this.y_padding / yStretchFactor;
      const popup = document.getElementById(`${this.componentType}-profile-coordinates-container`);
      if (popup) {
        if (this.maxExtent) {
          const viewYMax = this.maxExtent.ymax / yStretchFactor;
          const viewYMin = this.maxExtent.ymin / yStretchFactor;
          if ((Number(distance) > this.threeCrossSectionStartDist) && (Number(elevation) > (Number(viewYMin) + yPadding))) { // Note: threeCrossSectionStartDist is 0 for other components
            if (this.componentType === "waterway") {
              const formattedDistance = formatStationString(distance);
              popup.innerText = `(${formattedDistance}, ${elevation}, ${cutFill})`;
            } else {
              popup.innerText = `(${distance}, ${elevation})`;
            }

            let top;
            let left;

            const cursorX = movingPoint.x;
            const viewHalfWidth = this.maxExtent?.xmax / 2;
            const cursorY = movingPoint.y / yStretchFactor;
            const viewHalfHeight = viewYMax - ((viewYMax - (viewYMin + yPadding)) / 2);
            if (cursorX > viewHalfWidth && cursorY < viewHalfHeight) {
              left = (event.x - 6 - popup.offsetWidth).toString();
              top = (event.y - 6 - popup.offsetHeight).toString();
            } else if (cursorX < viewHalfWidth && cursorY > viewHalfHeight) {
              left = (event.x + 10).toString();
              top = (event.y + 10).toString();
            } else if (cursorX > viewHalfWidth && cursorY > viewHalfHeight) {
              left = (event.x - 6 - popup.offsetWidth).toString();
              top = (event.y + 10).toString();
            } else if (cursorX < viewHalfWidth && cursorY < viewHalfHeight) {
              left = (event.x + 6).toString();
              top = (event.y - 6 - popup.offsetHeight).toString();
            }

            popup.style.left = `${left}px`;
            popup.style.top = `${top}px`;
            popup.style.visibility = 'visible';
          } else {
            popup.style.visibility = 'hidden';
          }
        }
      }
    }
  }

  private getCenterlineZByDistance(distance) {
    const centerlineProfile = this.profiles[ProfileKeyCode[4]] as DynamicProfileClass;
    const groundProfilePoints = centerlineProfile.groundProfilePoints;

    for (const point of groundProfilePoints) {
      if (point[0] === distance) {
        return point[1]
      }
    }

    for (let i = 1; i < groundProfilePoints.length; i++) {
      if (groundProfilePoints[i - 1][0] < distance && distance < groundProfilePoints[i][0]) {
        const x0 = groundProfilePoints[i - 1][0], y0 = groundProfilePoints[i - 1][1];
        const x1 = groundProfilePoints[i][0], y1 = groundProfilePoints[i][1];
        const groundZ = y0 + (distance - x0) * (y1 - y0) / (x1 - x0);
        return groundZ;
      }
    }

    return null
  }

  cleanMapUp() {
    for (const key in this.profiles) {
      const profile = this.profiles[key] as DynamicProfileClass;
      profile?.Dispose();
    }
  }

  Dispose() {
    if (this.newVertexReactionDisposer) this.newVertexReactionDisposer();
    if (this.mapSketchReationDispoer) this.mapSketchReationDispoer();
    if (this.insertVertexReactionDisposer) this.insertVertexReactionDisposer();
    if (this.moveVertexReactionDisposer) this.moveVertexReactionDisposer();
    if (this.deleteVertexReactionDisposer) this.deleteVertexReactionDisposer();
    if (this.sketchCancelReactionDisposer) this.sketchCancelReactionDisposer();

    this.cleanMapUp();

    this._profileView?.destroy();
    this.mainSketchVm?.destroy();
    this.stopCoordinateReadout();
  }

  // ai design experimental code
  /* istanbul ignore next */
  public toggleReaches(on: boolean) {
    const reachStationsLayer = this._profileView?.map.findLayerById('reachStations');
    if (reachStationsLayer) reachStationsLayer.visible = on;

    const reachLabelLayer = this._profileView?.map.findLayerById('reachLabelFeatureLayer');
    if (reachLabelLayer) reachLabelLayer.visible = on;

    const startEndLabelFeatureLayer = this._profileView?.map.findLayerById('startEndLabelFeatureLayer');
    if (startEndLabelFeatureLayer) startEndLabelFeatureLayer.visible = on;

    if (this.sketchLayer && !on) this.sketchLayer.graphics.removeAll();
  }

  // End: ai design experimental code
}

export { DynamicProfileStoreBase };
